import { type JanusCtx, type Row, handBack, insertRoll, isInventoryRow } from '../context.js';
import { effectiveAbilityDc } from '../effective-ability-dc.js';
import { phpJsonTruthy } from '../../compile/php-json-values.js';
import { isPhpNumeric } from '../../rolls/dice-roll.js';
import { type JanusInput } from '../janus.js';
import { selectSkillForContestingRoll } from '../select-skill-for-contesting-roll.js';
import { castSkillEntries } from '../charged-cast.js';
import { skillRollArr } from '../skill-roll.js';
import { damageTypeRoll, withFragmentGateStatuses } from './damage-type-roll.js';
import { postEffectConditions } from '../post-effect-conditions.js';
import { nullifyPostEffectConditions } from '../nullify-post-effect-conditions.js';
import { damagingForcedMove } from '../movement/check-push-and-pull-after-hit.js';
import { effectTargetProceeds } from '../is-target-valid-for-effect.js';
import { contestDirectionalCover } from '../cover/directional-cover.js';
import { isPaoeEntry } from '../paoe-entry-mode.js';
import { applyRandomTablePayload, hasRandomTablePayload } from '../random-table-payload.js';
import { CONTESTING_ROLL, chainLeapDamage, chainSpec } from './chain-leap.js';
import { selectCastingSkill } from '../summon-caster-cantrips.js';
import { contestSkippedForWillingTarget } from '../check-opposing-team.js';

/** includes/functions/janus/rolls/contesting_roll.php:contesting_roll */
export function contestingRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[],
  effectsOnWin?: (target: Row) => boolean): void {
  if (!ctx.read.has(ability, 'skill_roll_contestee')) handBack('contesting_roll:no-contestee');
  const tick = input.useAbilityVariable1 === 'dot_status_tick';
  // A lingering contest rolls its save and damage, without replaying cast/on-hit payloads.
  const damageInput = tick ? { ...input, useAbilityVariable1: 'damage_only' } : input;
  const payload = ['immune_to_ability_upon_successful_contesting_roll_till_long_rest',
    'caster_moves_to_target_in_ft', 'target_moves_from_caster_in_ft', 'ability_moves_to_target_in_ft'];
  for (const key of payload) if (!tick && ctx.read.has(ability, key)) handBack(`contesting_roll:payload:${key}`);
  // Fixed DCs produce a caster row without a draw; adjust_dc overrides the effective DC last.
  let casterResult: number;
  const dc = effectiveAbilityDc(ctx, caster, ability);
  if (dc !== null) {
    const adjust = input.useAbilityVariable1 === 'adjust_dc' && phpJsonTruthy(input.useAbilityVariable2)
      && isPhpNumeric(input.useAbilityVariable2);
    // The shared commit stores integer roll rows; fractional adjustments retain their named refusal.
    if (adjust && !Number.isSafeInteger(Number(input.useAbilityVariable2))) handBack('contesting_roll:fixed-dc-adjusted');
    casterResult = adjust ? Number(input.useAbilityVariable2) : dc;
    insertRoll(ctx, { roll_type_1: 'AD80D31D-898B-66CE-0894-1335D11B1529', roll_type_2: 'ability_dc', roll_item_id: input.abilityId, by: caster, roll_result: casterResult });
  } else {
    // summons_gain_caster_cantrips: an injected cantrip contests with the CONTROLLER's skill (the summon still rolls the d20)
    const casterSkill = selectCastingSkill(ctx, caster, input.abilityId, castSkillEntries(ctx, ctx.read.entries(ability, 'skill_roll_contester')));
    casterResult = skillRollArr(ctx, input, caster, casterSkill, null, 0, { isTarget: false });
  }
  // damage_roll()'s deferred payload block (roll_random_table / contingent_ability on a CONTESTED ability — lane A3 RT-4): the targets the
  // contest LANDED on (caster win + post_effect_conditions met — the same gate the contest-failed statuses use), after the whole loop.
  const landed: Row[] = [];
  // CHAIN (chain_targets_max, R-175 — chain-leap.ts): only the FIRST pick is contested; the rest are leap targets, damaged after the loop
  // when the primary's contest LANDED. A zone entry / a DoT contest never chains.
  // ft_chain_leap_split(): a damage-only / DoT-tick / zone re-dispatch never chains (its picks stay ordinary targets).
  const chain = ['dot_status_tick', 'damage_only', 'end_turn_in_p_aoe', 'moving_into_p_aoe'].includes(String(input.useAbilityVariable1 ?? ''))
    ? null : chainSpec(ctx.read, ability);
  let contested = targets;
  let leaps: Row[] = [];
  if (chain && targets.length > 1) {
    if (String(ctx.read.value(ability, 'roll_type') ?? '').toUpperCase() !== CONTESTING_ROLL || isPaoeEntry(input)
        || ctx.read.has(ability, 'aoe_type') || ctx.read.has(ability, 'persistent_aoe')) handBack('chain_leap:host-shape');
    if (targets.length - 1 > chain.max) handBack('chain_leap:too-many-targets');
    contested = [targets[0]!];
    leaps = targets.slice(1);
  }
  for (const target of contested) {
    if (!effectTargetProceeds(ctx, input, ability, caster, target, target === targets[0])) continue;
    // damage_roll.php synthesizes success (1 >= 0) for an inventory target; objects never contest.
    const inventory = isInventoryRow(target);
    // damage_roll.php:336–449: willingness keeps the caster row, skips target dice/passives and synthesizes 1 >= 0.
    const willing = contestSkippedForWillingTarget(ctx, ability, caster, target);
    const contest = inventory || willing ? { autoWin: false, targetResult: 0 } : (() => {
      const targetSkill = selectSkillForContestingRoll(ctx, target, ctx.read.entries(ability, 'skill_roll_contestee'));
      const cover = contestDirectionalCover(ctx, ability, caster, target, targetSkill.id);
      targetSkill.adv += cover.advStacks;
      return { autoWin: cover.autoWin,
        targetResult: skillRollArr(ctx, input, target, targetSkill, caster, casterResult, { asTarget: true, isTarget: true }) };
    })();
    // A tie is a caster win; a lost contest prepares damage only with half damage authored.
    const casterWon = inventory || willing || (!contest.autoWin && casterResult >= contest.targetResult);
    // A contesting zone's entry remains status-only; a DoT zone keeps its damage.
    const zoneEntry = isPaoeEntry(input) && String(ctx.read.value(ability, 'roll_type') ?? '').toUpperCase() === 'AD80D31D-898B-66CE-0894-1335D11B1529';
    const damages = ctx.read.has(ability, 'damage_type') && (casterWon || ctx.read.has(ability, 'contesting_roll_successful_for_half_damage'));
    const half = !casterWon && ctx.read.has(ability, 'contesting_roll_successful_for_half_damage');
    const effects = (): boolean => {
      if (!casterWon || tick) return true;
      const met = effectsOnWin ? effectsOnWin(target) : postEffectConditions(ctx, input, ability, caster, target, true);
      if (met) landed.push(target);
      return met;
    };
    // damage_roll.php:731 prepares damage before its post_effect_conditions call (:839).
    // post_effect_conditions.php:1096–1124 skips only application when the recipient gate is unmet.
    // Hold fragment status gates before effects (ruling 65); the application uses live state.
    const atDraw = withFragmentGateStatuses(ctx, target);
    // Probe the shared gate without publishing rows/warnings or running effects. Keep the met
    // path: `between` cannot yet prepare some damage-modifying statuses that the effects remove.
    const deferEffects = !tick && damages && !zoneEntry && (damagingForcedMove(ctx, ability)
      || (casterWon && !nullifyPostEffectConditions({ ...ctx, rows: [], dryRun: true }, ability, caster, target)));
    if (deferEffects) {
      atDraw(() => damageTypeRoll(ctx, damageInput, ability, caster, target, half, false, 0, false, effects));
      continue;
    }
    effects();
    if (zoneEntry) continue;
    if (damages) atDraw(() => damageTypeRoll(ctx, damageInput, ability, caster, target, half));
    else if (!ctx.read.has(ability, 'damage_type') && ctx.read.has(ability, 'contesting_roll_successful_for_half_damage'))
      handBack('contesting_roll:half-damage-without-type');
  }
  if (chain && leaps.length && landed.includes(contested[0]!)) chainLeapDamage(ctx, input, ability, caster, contested[0]!, leaps, chain);
  if (!tick && hasRandomTablePayload(ability)) applyRandomTablePayload(ctx, input, ability, caster, contested, landed);
}
